import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  UploadFile, VisibilityOutlined, FileDownloadOutlined, DeleteOutline, WorkspacePremiumOutlined
} from '@mui/icons-material';
import PdfViewModal from 'components/shared/modals/PdfViewModal';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import StyledProfileTabPanelTitle from 'common/theme/profile/StyledProfileTabPanelTitle';
import { downloadFile, createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const MAX_SIZE = 15 * 1024 * 1024; // 15MB

const LIST = gql`
  query MyTrainerCertifications {
    myTrainerCertifications { id name file_url created_at }
  }
`;
const UPLOAD = gql`
  mutation UploadTrainerCertification($name: String, $certificate: Upload!) {
    uploadTrainerCertification(name: $name, certificate: $certificate) { id name file_url created_at }
  }
`;
const DELETE = gql`
  mutation DeleteTrainerCertification($id: ID!) {
    deleteTrainerCertification(id: $id)
  }
`;

/**
 * Certificaciones del entrenador como ARCHIVOS: listar + subir (PDF ≤15MB) + ver embebido +
 * descargar + borrar. Autónomo (apollo), se renderiza dentro del perfil profesional.
 */
function TrainerCertifications() {
  const { t } = useTranslation(['labels', 'buttons', 'errors']);
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [name, setName] = useState('');
  const [viewer, setViewer] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const { data, loading: listLoading, refetch } = useQuery(LIST, { fetchPolicy: 'cache-and-network' });
  const certifications = data?.myTrainerCertifications || [];

  const snack = (variant, message) => dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `cert-${variant}-${Date.now()}`, variant, message })));

  const [uploadCert, { loading: uploading }] = useMutation(UPLOAD, {
    onCompleted: () => { setName(''); refetch(); snack('success', t('certificationUploaded', { ns: 'labels', defaultValue: 'Certificación cargada' })); },
    onError: () => snack('error', t('certificationUploadError', { ns: 'errors', defaultValue: 'No se pudo cargar la certificación' }))
  });
  const [deleteCert] = useMutation(DELETE, {
    onCompleted: () => { refetch(); snack('success', t('certificationDeleted', { ns: 'labels', defaultValue: 'Certificación eliminada' })); },
    onError: () => snack('error', t('certificationDeleteError', { ns: 'errors', defaultValue: 'No se pudo eliminar' }))
  });

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { snack('error', t('onlyPdf', { ns: 'errors', defaultValue: 'Solo se permiten archivos PDF' })); return; }
    if (file.size > MAX_SIZE) { snack('error', t('upload.size.file', { ns: 'errors' })); return; }
    uploadCert({ variables: { name: name.trim() || file.name, certificate: file } });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <StyledProfileTabPanelTitle component="h4">
        {t('certifications', { ns: 'labels', defaultValue: 'Certificaciones' })}
      </StyledProfileTabPanelTitle>

      {/* Alta */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onPick} />
        <TextField
          size="small"
          fullWidth
          label={t('certificationName', { ns: 'labels', defaultValue: 'Nombre de la certificación' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          variant="contained"
          color="secondary"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          sx={{ whiteSpace: 'nowrap', height: 40 }}
        >
          {t('addCertification', { ns: 'buttons', defaultValue: 'Agregar certificación' })}
        </Button>
      </Stack>

      {listLoading && certifications.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <Stack spacing={1.25}>
          {certifications.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('noCertifications', { ns: 'labels', defaultValue: 'Aún no has cargado certificaciones.' })}
            </Typography>
          ) : certifications.map((c) => (
            <Stack
              key={c.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'surface.raised', border: '1px solid', borderColor: 'hairline.main', borderLeft: '4px solid', borderLeftColor: 'gold.main' }}
            >
              <WorkspacePremiumOutlined sx={{ color: 'gold.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1, minWidth: 0 }} noWrap>
                {c.name || c.file_url.split('/').at(-1)}
              </Typography>
              <Tooltip title={t('view', { ns: 'buttons', defaultValue: 'Ver' })}>
                <IconButton size="small" onClick={() => setViewer(c)} sx={{ color: 'gold.main', '&:hover': { backgroundColor: 'gold.soft' } }}>
                  <VisibilityOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('download', { ns: 'buttons', defaultValue: 'Descargar' })}>
                <IconButton size="small" onClick={() => downloadFile(c.file_url, c.name || 'certificacion.pdf')} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                  <FileDownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}>
                <IconButton size="small" onClick={() => setToDelete(c)} sx={{ color: (th) => th.palette.sessionStatus.absent.fg }}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}

      <PdfViewModal open={Boolean(viewer)} src={viewer?.file_url} title={viewer?.name} filename={viewer?.name} onClose={() => setViewer(null)} />
      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => { if (toDelete) deleteCert({ variables: { id: toDelete.id } }); setToDelete(null); }}
        title={t('deleteCertificationTitle', { ns: 'labels', defaultValue: 'Eliminar certificación' })}
        message={t('deleteCertificationMessage', { ns: 'labels', defaultValue: '¿Seguro que quieres eliminar esta certificación?' })}
      />
    </Box>
  );
}

export default TrainerCertifications;
